/**
 * The name of the native browser `CustomEvent` dispatched on `window` whenever a
 * confirmation dialog should be shown.
 *
 * Using a native browser event (instead of a redux action) keeps the producer fully
 * decoupled from React and redux: any code — including non-React code, redux epics, or
 * services — can request a confirmation dialog without depending on the modal context.
 * A listener mounted inside `ModalDialogProvider` turns the event into a rendered dialog
 * and settles the promise returned by {@link dispatchConfirmationDialog} with the user's
 * choice.
 */
export const confirmationDialogEventName = 'famly/confirmation-dialog';
/**
 * Dispatches a confirmation dialog request as a native browser `CustomEvent` on `window`
 * and resolves once the user confirms or cancels.
 *
 * A listener mounted inside `ModalDialogProvider` renders the dialog and settles the
 * returned promise. If no provider is mounted, the promise never settles.
 *
 * This is the modern, redux-free replacement for the legacy `showDialog` helper.
 *
 * @param options the confirmation dialog to show
 * @returns a promise resolving to `'OK'` when confirmed or `'CANCEL'` when dismissed
 *
 * @example
 * const choice = await dispatchConfirmationDialog({
 *     title: i18next.t($ => $.areYouSure),
 *     description: i18next.t($ => $.product.productDeleteDescription),
 *     confirmText: i18next.t($ => $.delete),
 *     variant: 'critical',
 * });
 * if (choice === 'OK') {
 *     // perform the action
 * }
 */
export function dispatchConfirmationDialog(options) {
    return new Promise(resolve => {
        window.dispatchEvent(new CustomEvent(confirmationDialogEventName, {
            detail: { ...options, resolve },
        }));
    });
}
